import { API } from "./api";

/** Extract a human-readable message from FastAPI error responses. */
function parseApiError(data: any, fallback: string): string {
    if (!data) return fallback;
    // Pydantic validation: detail is an array of { msg, loc, ... }
    if (Array.isArray(data.detail)) {
        return data.detail.map((e: any) => e.msg?.replace(/^Value error, /i, "") || e.msg).join(". ");
    }
    // Standard FastAPI: detail is a string
    if (typeof data.detail === "string") return data.detail;
    return fallback;
}

export const login = (username: string, password: string) =>
    fetch(`${API}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            username,
            password,
            grant_type: 'password',
        }).toString(),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Login failed: ${response.status}`));
        }
        return response.json();
    });

export const refreshToken = (refreshToken: string) =>
    fetch(`${API}/token/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
        }).toString(),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Token refresh failed: ${response.status}`));
        }
        return response.json();
    });

export const setPassword = (token: string, newPassword: string) =>
    fetch(`${API}/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Set password failed: ${response.status}`));
        }
        return response.json();
    });

export const forgotPassword = (email: string) =>
    fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Request failed: ${response.status}`));
        }
        return response.json();
    });

export const resetPassword = (token: string, newPassword: string) =>
    fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Reset password failed: ${response.status}`));
        }
        return response.json();
    });

export const changePassword = (authToken: string, currentPassword: string, newPassword: string) =>
    fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Change password failed: ${response.status}`));
        }
        return response.json();
    });

export interface Setup2faResponse {
    qr_code_base64: string;
    secret: string;
    provisioning_uri: string;
    recovery_codes: string[];
}

export const setup2fa = (authToken: string): Promise<Setup2faResponse> =>
    fetch(`${API}/auth/2fa/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `2FA setup failed: ${response.status}`));
        }
        return response.json();
    });

export const verify2fa = (authToken: string, totpCode: string) =>
    fetch(`${API}/auth/2fa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ totp_code: totpCode }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `2FA verification failed: ${response.status}`));
        }
        return response.json();
    });

export const disable2fa = (authToken: string) =>
    fetch(`${API}/auth/2fa`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `Disable 2FA failed: ${response.status}`));
        }
        return response.json();
    });

export const verifyMfaLogin = (mfaToken: string, totpCode?: string, recoveryCode?: string) =>
    fetch(`${API}/auth/login/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            mfa_token: mfaToken,
            totp_code: totpCode || null,
            recovery_code: recoveryCode || null,
        }),
    }).then(async response => {
        if (!response.ok) {
            const data = await response.json().catch(() => null);
            throw new Error(parseApiError(data, `MFA verification failed: ${response.status}`));
        }
        return response.json();
    });
